import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Banner,
  DataTable,
  Box,
  InlineGrid,
  SkeletonBodyText,
} from '@shopify/polaris';
import {
  useCrawlerAnalyticsStats,
} from '../../hooks/useCrawlerAnalytics';
import { formatRelativeOrDateUTC } from '../../utils/dateFormat';

/**
 * F4.3: Crawler Analytics Dashboard — displays AI crawler access stats,
 * daily trend, and selective allow/deny controls.
 */
export function CrawlerAnalyticsDashboard() {
  const { data: stats, isLoading: statsLoading, isError: statsError } = useCrawlerAnalyticsStats(7);

  if (statsLoading) {
    return (
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">AI Crawler Analytics</Text>
          <SkeletonBodyText lines={5} />
        </BlockStack>
      </Card>
    );
  }

  if (statsError || !stats) {
    return (
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">AI Crawler Analytics</Text>
          <Banner tone="critical">
            Unable to load crawler analytics. This feature requires Growth plan or higher.
          </Banner>
        </BlockStack>
      </Card>
    );
  }

  return (
    <BlockStack gap="400">
      {/* Summary Stats */}
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h2" variant="headingMd">AI Crawler Analytics</Text>
            <Badge tone="info">{`Last ${stats.periodDays} days`}</Badge>
          </InlineStack>

          <InlineGrid columns="2" gap="400">
            <StatCard
              label="Total Accesses"
              value={stats.totalAccesses.toLocaleString()}
            />
            <StatCard
              label="Unique Crawlers"
              value={String(stats.crawlers.length)}
            />
          </InlineGrid>
        </BlockStack>
      </Card>

      {/* Crawler Breakdown */}
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">Crawler Breakdown</Text>
          {stats.crawlers.length === 0 ? (
            <Text as="p" tone="subdued">
              No AI crawler accesses detected yet. Once crawlers visit your
              llms.txt and feed, their activity will appear here.
            </Text>
          ) : (
            <DataTable
              columnContentTypes={['text', 'numeric', 'text']}
              headings={['Crawler', 'Accesses', 'Last Seen']}
              rows={stats.crawlers.filter((c) => c.crawlerName !== 'Unknown').map((c) => [
                c.crawlerName,
                c.accessCount.toLocaleString(),
                c.lastAccessedAt
                  ? formatRelativeOrDateUTC(c.lastAccessedAt)
                  : '—',
              ])}
            />
          )}
        </BlockStack>
      </Card>

      {/* Daily Trend */}
      {stats.dailyTrend.length > 0 && (
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">Daily Access Trend (Last 7 days)</Text>
            <DataTable
              columnContentTypes={['text', 'numeric']}
              headings={['Date', 'Accesses']}
              rows={stats.dailyTrend.slice(-7).map((d) => [
                d.date,
                d.count.toLocaleString(),
              ])}
            />
          </BlockStack>
        </Card>
      )}
    </BlockStack>
  );
}

/** Simple stat card for the summary row. */
function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Box
      padding="400"
      borderWidth="025"
      borderRadius="200"
      borderColor="border"
    >
      <BlockStack gap="100">
        <Text as="p" variant="bodySm" tone="subdued">{label}</Text>
        <Text as="p" variant="headingLg">{value}</Text>
      </BlockStack>
    </Box>
  );
}
